import { api } from "@/services/api"
import { useQuery } from "@tanstack/vue-query"
import type { MaybeRef } from "@vueuse/core"
import type { MerchantCurrencyCode } from "@/utils/currency"
import type { Source } from "../sharedTypes/merchants"
import type { TableOrdering } from "@/components/BaseTable.vue"

type CriticalEngagementAlert =
  | "not_transacted"
  | "stopped_transacting"

export type Merchant = {
  id: number
  merchantName: string
  merchantId: string
  tpReference: string
  transactionTotal: number
  lastTransactedAt: Date | null
  portalLink: string | null
  beepaidgoLink: string | null
  merchantGroup: {
    name: string
    id: string
  } | null
  sources: Source[] | null
  criticalEngagementAlerts: CriticalEngagementAlert[]
  isCriticalAlertsSupported: boolean
  engagementScore: number | null
  currency: MerchantCurrencyCode
  isActive: boolean

  hasSalesModule: boolean
  hasDonationsModule: boolean
  hasBeepaidOrderModule: boolean
  hasBeepaidGoModule: boolean
}

const parseMerchant = (
  merchant: RecursivelyConvertDatesToStrings<Merchant>
): Merchant => ({
  ...merchant,
  lastTransactedAt: merchant.lastTransactedAt
    ? DateFns.parseISO(merchant.lastTransactedAt)
    : null,
})

export type MerchantFilter = {
  search?: string
  searchFields?: ReadonlyArray<
    "merchant_name" | "tp_reference" | "pax_merchant_id"
  >
  includeDemoAndDisabled?: boolean
}

export type ListMerchantsOrdering =
  | "engagementScore"
  | "transactionTotal"
  | "lastTransactedAt"
  | "merchantName"

type MerchantQuery = MerchantFilter & {
  ordering?: TableOrdering<ListMerchantsOrdering>
} & Pagination

type ListMerchantsResponse = Paginate<Merchant[]> & {
  context: {
    merchantStatsRefreshedAt: Date
  }
}

const listMerchants = async (
  query: MerchantQuery
): Promise<ListMerchantsResponse> => {
  const { body } = await api.get<
    RecursivelyConvertDatesToStrings<ListMerchantsResponse>
  >("/merchants/", {
    query: query,
  })

  return {
    ...body,
    items: body.items.map((merchant) =>
      parseMerchant(merchant)
    ),
    context: {
      merchantStatsRefreshedAt: DateFns.parseISO(
        body.context.merchantStatsRefreshedAt
      ),
    },
  }
}

export const useMerchants = (
  query: MaybeRef<MerchantQuery>
) =>
  useQuery({
    queryKey: [MERCHANTS_QUERY_KEY, query],
    queryFn: ({ queryKey: [_, query_] }) =>
      listMerchants(unref(query_) as MerchantQuery),
    keepPreviousData: true,
  })
